import { create } from '@bufbuild/protobuf'
import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BidLimitCell } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { BidLimitCellSchema } from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'

import { clampBidLimitCellValue } from '~/pages/AdminBidLimits/models/bidLimitSchema'
import type { CellKey } from '~/pages/AdminBidLimits/models/cellKey'
import { makeCellKey } from '~/pages/AdminBidLimits/models/cellKey'

/**
 * セル値の union type。
 * "unlimited" = value フィールド unset (= proto semantics で「制限なし」明示)
 * "limited"   = value フィールドあり (= 具体的な制限値)
 */
export type BidLimitValue =
  | { kind: 'unlimited' }
  | { kind: 'limited'; value: number }

/**
 * MatrixDraft: segment 単位の draft state。
 * cells は決定済みのセルのみ格納 (= absent = undecided)。
 */
export type MatrixDraft = {
  cells: Map<CellKey, BidLimitValue>
}

/** 空の MatrixDraft */
export function emptyMatrixDraft(): MatrixDraft {
  return { cells: new Map() }
}

/**
 * BFF response の BidLimitCell[] から MatrixDraft を作成する。
 * segment で絞ってから変換する。
 */
export function matrixDraftFromCells(
  cells: BidLimitCell[],
  segment: BidSegment,
): MatrixDraft {
  const map = new Map<CellKey, BidLimitValue>()
  for (const cell of cells) {
    if (cell.bidSegment !== segment) continue
    const key = makeCellKey(cell.areaCode, cell.side, cell.kind)
    if (cell.value === undefined) {
      map.set(key, { kind: 'unlimited' })
    } else {
      const numStr = cell.value.value
      map.set(key, { kind: 'limited', value: parseFloat(numStr) })
    }
  }
  return { cells: map }
}

/**
 * MatrixDraft + segment → BFF UpdateBidLimitMatrix の cells[] に変換する。
 * Map.entries() をそのまま BidLimitCell に展開する。
 */
export function matrixDraftToCells(
  draft: MatrixDraft,
  segment: BidSegment,
): BidLimitCell[] {
  const result: BidLimitCell[] = []
  for (const [key, value] of draft.cells.entries()) {
    const { areaCode, side, kind } = parseCellKeyLocal(key)
    const cell = create(BidLimitCellSchema, {
      bidSegment: segment,
      areaCode,
      side,
      kind,
      value:
        value.kind === 'limited'
          ? create(DecimalSchema, { value: String(value.value) })
          : undefined,
    })
    result.push(cell)
  }
  return result
}

/**
 * draft の全セルを値域内・刻みの桁へクランプした新しい draft を返す
 * (`docs/specs/bidding/bid-limits.md` AC-BDL-007)。
 *
 * SoT (mock-app `BidSettings.razor.cs` の `MarkLimitEdit` / design
 * `admin/bid-limits.html` の `_clampLimitValue`) は書き込み経路が 1 本で、
 * 状態には常にクランプ済みの値しか入らない。 実装は打鍵中だけ生値を許す
 * (打鍵ごとに丸めると 「0.05」 の先頭の 「0」 が 0.1 に化けて打てない) ため、
 * 保存境界でこの不変条件へ揃える。 未決定 (セル無し) と未設定明示
 * (`unlimited`) はクランプ対象外。
 */
export function clampMatrixDraft(draft: MatrixDraft): MatrixDraft {
  const cells = new Map(draft.cells)
  for (const [key, value] of draft.cells.entries()) {
    if (value.kind !== 'limited') continue
    const { kind } = parseCellKeyLocal(key)
    const clamped = clampBidLimitCellValue(value.value, kind)
    if (clamped !== value.value) {
      cells.set(key, { kind: 'limited', value: clamped })
    }
  }
  return { cells }
}

/** matrix draft が saved snapshot と異なるか (= dirty 判定) */
export function isMatrixDirty(draft: MatrixDraft, saved: MatrixDraft): boolean {
  if (draft.cells.size !== saved.cells.size) return true
  for (const [key, val] of draft.cells.entries()) {
    const savedVal = saved.cells.get(key)
    if (savedVal === undefined) return true
    if (val.kind !== savedVal.kind) return true
    if (
      val.kind === 'limited' &&
      savedVal.kind === 'limited' &&
      val.value !== savedVal.value
    )
      return true
  }
  return false
}

/** マトリクスの売買列グループ (表示順)。 */
export const MATRIX_SIDES: readonly Side[] = [Side.BUY, Side.SELL]

const ALL_MATRIX_KINDS: readonly BidKind[] = [
  BidKind.MIN,
  BidKind.MAX,
  BidKind.QTY,
  BidKind.QTY_TOTAL,
]

/**
 * segment が持つ kind の列 (表示順)。 列の組み立て・ブロック必須化の対象は
 * すべてここから導く。 スポットのコマ計は通常・ブロック共通の 1 値で通常入札
 * タブが持つため、 SPOT_BLOCK は QTY_TOTAL を持たない (#8479。 backend
 * `bidlimit.SegmentHoldsKind` と同じ規則)。
 */
export function matrixKindsOf(segment: BidSegment): readonly BidKind[] {
  return segment === BidSegment.SPOT_BLOCK
    ? ALL_MATRIX_KINDS.filter((kind) => kind !== BidKind.QTY_TOTAL)
    : ALL_MATRIX_KINDS
}

/** kind の列名の i18n key。 */
export function matrixKindLabelKey(
  kind: BidKind,
):
  | 'page:adminBidLimits.matrix.minPrice'
  | 'page:adminBidLimits.matrix.maxPrice'
  | 'page:adminBidLimits.matrix.maxQty'
  | 'page:adminBidLimits.matrix.maxQtyTotal' {
  switch (kind) {
    case BidKind.MIN:
      return 'page:adminBidLimits.matrix.minPrice'
    case BidKind.MAX:
      return 'page:adminBidLimits.matrix.maxPrice'
    case BidKind.QTY:
      return 'page:adminBidLimits.matrix.maxQty'
    default:
      return 'page:adminBidLimits.matrix.maxQtyTotal'
  }
}

/** SPOT_BLOCK matrix で未決定のセル (areaCode, side, kind) の一覧を返す */
export function findMissingBlockCells(
  draft: MatrixDraft,
): Array<{ areaCode: number; side: Side; kind: BidKind }> {
  const missing: Array<{ areaCode: number; side: Side; kind: BidKind }> = []
  const areaCodes = [1, 2, 3, 4, 5, 6, 7, 8, 9]
  for (const areaCode of areaCodes) {
    for (const side of MATRIX_SIDES) {
      for (const kind of matrixKindsOf(BidSegment.SPOT_BLOCK)) {
        const key = makeCellKey(areaCode, side, kind)
        if (!draft.cells.has(key)) {
          missing.push({ areaCode, side, kind })
        }
      }
    }
  }
  return missing
}

// ローカル用 parseCellKey (循環依存を避けるため inline)
function parseCellKeyLocal(key: CellKey): {
  areaCode: number
  side: Side
  kind: BidKind
} {
  const [areaCode, side, kind] = key.split('|').map(Number)
  return { areaCode, side: side as Side, kind: kind as BidKind }
}
